import { cn } from '@/lib/utils'
import type { Notification, NotificationEventType } from '@/types/notification'
import { formatDistanceToNow } from 'date-fns'
import {
    AlertTriangle,
    BarChart3,
    CreditCard,
    Megaphone,
    Shield,
    TrendingDown,
    Wallet,
    Wrench,
} from 'lucide-react'

const eventIcons: Record<NotificationEventType, React.ElementType> = {
  budget_alert: Wallet,
  conversion_alert: TrendingDown,
  performance_alert: BarChart3,
  campaign_status: Megaphone,
  billing_alert: CreditCard,
  security_alert: Shield,
  system_update: Wrench,
  weekly_report: BarChart3,
}

const eventColors: Record<NotificationEventType, string> = {
  budget_alert: 'text-amber-500',
  conversion_alert: 'text-emerald-500',
  performance_alert: 'text-blue-500',
  campaign_status: 'text-ghost-500',
  billing_alert: 'text-orange-500',
  security_alert: 'text-red-500',
  system_update: 'text-slate-500',
  weekly_report: 'text-blue-500',
}

interface NotificationItemProps {
  notification: Notification
  onClick?: (notification: Notification) => void
  onDelete?: (id: string) => void
  compact?: boolean
}

export function NotificationItem({ notification, onClick, onDelete, compact = false }: NotificationItemProps) {
  const Icon = eventIcons[notification.eventType] ?? AlertTriangle
  const iconColor = eventColors[notification.eventType] ?? 'text-muted-foreground'
  const isUnread = !notification.readAt

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors rounded-md',
        'hover:bg-muted/50',
        isUnread && 'bg-ghost-500/5',
        compact && 'px-2 py-2',
      )}
      onClick={() => onClick?.(notification)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.(notification)
        }
      }}
    >
      <div className={cn('mt-0.5 shrink-0', iconColor)}>
        <Icon className={cn('h-4 w-4', compact && 'h-3.5 w-3.5')} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={cn(
            'text-sm truncate',
            isUnread ? 'font-medium text-foreground' : 'text-muted-foreground',
          )}>
            {notification.title}
          </p>
          {isUnread && (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ghost-500" />
          )}
        </div>
        {!compact && (
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {notification.message}
          </p>
        )}
        <p className="text-xs text-muted-foreground/60 mt-0.5">{timeAgo}</p>
      </div>

      {onDelete && (
        <button
          type="button"
          className="shrink-0 mt-0.5 text-muted-foreground/40 hover:text-destructive transition-colors"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(notification.id)
          }}
          aria-label="Delete notification"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}
